import { Input } from '@/components/ui/input';
import { ALISA_IMAGES } from '@/data/moduleData';
import { useGameStore } from '@/store/useGameStore';
import { motion } from 'framer-motion';
import { ArrowRight, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { useLangNavigate, useT } from '@/i18n';

export default function SplashScreen() {
  const [name, setName] = useState('');
  const [step, setStep] = useState('welcome');
  const navigate = useLangNavigate();
  const t = useT();
  const setUserName = useGameStore((s) => s.setUserName);
  const isRegistered = useGameStore((s) => s.isRegistered);
  const hasHydrated = useGameStore((s) => s._hasHydrated);

  // If already registered, redirect
  useEffect(() => {
    if (hasHydrated && isRegistered) {
      navigate('/home', { replace: true });
    }
  }, [hasHydrated, isRegistered, navigate]);

  const handleStart = () => {
    if (name.trim().length < 2) return;
    setUserName(name.trim());
    navigate('/home');
  };

  return (
    <div className="min-h-screen gradient-warm-bg flex justify-center">
      <div className="w-full max-w-mobile min-h-screen flex flex-col relative overflow-hidden" style={{ minHeight: '100dvh' }}>

        {/* Decorative gradient top - only on welcome screen */}
        {step === 'welcome' && (
          <div className="absolute top-0 left-0 right-0 h-40 gradient-hero opacity-90 rounded-b-[40px]" />
        )}

        {/* Переключатель языка стоит в углу, а не в потоке под подзаголовком:
            картинка Алисы поднята на -mt-36 и перекрывает всё, что окажется
            между подзаголовком и ею, — кнопки там не нажимались вовсе.
            Подпись светлая только пока под ней градиент, то есть на welcome. */}
        <LanguageSwitcher
          className="absolute top-3 right-4 z-20"
          variant={step === 'welcome' ? 'light' : 'dark'}
        />

        {/* Content */}
        <div className={`relative z-10 flex-1 flex flex-col px-6 ${step === 'name' ? 'pt-2' : 'pt-8'}`}>

          {step === 'welcome' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center text-center"
            >
              {/* Logo icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
                className="w-16 h-16 rounded-2xl gradient-primary-btn shadow-primary flex items-center justify-center mb-6"
              >
                <Truck size={32} className="text-primary-foreground" />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl font-extrabold font-heading text-primary-foreground mb-2"
              >
                Delivery Ace
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-primary-foreground/80 text-sm font-body mb-2"
              >
                {t('ui.splash.tagline')}
              </motion.p>

              {/* Alisa hero image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="w-[384px] h-[420px] mx-auto -mt-36 -mb-10"
              >
                <img
                  src="/images/Alisa_neutral.png"
                  alt={t('ui.alisa.name')}
                  className="w-full h-full object-contain object-bottom drop-shadow-lg scale-x-[-1]"
                />
              </motion.div>

              {/* Description card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="relative z-10 bg-card rounded-2xl p-5 shadow-card w-full mb-6"
              >
                <h2 className="text-base font-bold font-heading text-foreground mb-2">
                  {t('ui.splash.cardTitle')}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed font-body">
                  {t('ui.splash.cardText')}
                </p>
              </motion.div>

              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                onClick={() => setStep('name')}
                className="w-full gradient-primary-btn text-primary-foreground font-heading font-bold text-base py-3.5 rounded-xl shadow-primary hover:shadow-primary-hover active:scale-[0.98] transition-transform duration-150 uppercase tracking-wide"
              >
                {t('ui.splash.start')}
              </motion.button>
            </motion.div>
          )}

          {step === 'name' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex-1 flex flex-col items-center text-center mt-0 pb-8"
            >
              <div className="flex flex-col items-center w-full max-w-xs">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  className="w-64 h-64 mx-auto mb-6 overflow-hidden rounded-full border-4 border-primary/20"
                >
                  <img
                    src={ALISA_IMAGES.icon}
                    alt={t('ui.alisa.name')}
                    className="w-full h-full object-contain"
                  />
                </motion.div>

                <h2 className="text-2xl font-bold font-heading text-foreground mb-2">
                  {t('ui.splash.imAlisa')}<br />{t('ui.splash.yourName')}
                </h2>
                <p className="text-sm text-muted-foreground mb-8 font-body">
                  {t('ui.splash.introducePrompt')}
                </p>

                <Input
                  type="text"
                  placeholder={t('ui.splash.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleStart()}
                  className="text-center text-base py-3 rounded-xl border-2 border-border focus:border-primary bg-card font-body mb-4"
                  autoFocus
                />
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={handleStart}
                  disabled={name.trim().length < 2}
                  className={`w-full flex items-center justify-center gap-2 gradient-primary-btn text-primary-foreground font-heading font-bold text-base py-3.5 rounded-xl shadow-primary uppercase tracking-wide transition-all duration-200 ${name.trim().length < 2 ? 'opacity-40 cursor-not-allowed' : 'hover:shadow-primary-hover active:scale-[0.98]'
                    }`}
                >
                  {t('ui.common.continue')}
                  <ArrowRight size={18} />
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
